import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Auto-logout after a period of no user interaction. Drop into any authenticated
 * page. An optional `onTimeout` runs just before logout — e.g. to release a lock.
 *
 * @param {object} [opts]
 * @param {number} [opts.timeoutMs=300000]  idle window before logout
 * @param {() => void} [opts.onTimeout]      cleanup fired right before logout
 */
export function useInactivityLogout({ timeoutMs = DEFAULT_TIMEOUT_MS, onTimeout } = {}) {
    const { logout } = useAuth();
    const navigate = useNavigate();

    // Keep latest callbacks in refs so the effect runs once and never goes stale.
    const logoutRef = useRef(logout);
    logoutRef.current = logout;
    const onTimeoutRef = useRef(onTimeout);
    onTimeoutRef.current = onTimeout;

    useEffect(() => {
        let lastActivity = Date.now();
        const bump = () => { lastActivity = Date.now(); };
        const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

        const check = setInterval(() => {
            if (Date.now() - lastActivity <= timeoutMs) return;
            clearInterval(check);
            events.forEach((e) => window.removeEventListener(e, bump));
            try { onTimeoutRef.current?.(); } catch { /* best-effort */ }
            toast.error('Logged out due to inactivity');
            logoutRef.current?.();
            navigate('/login');
        }, 30000);

        return () => {
            clearInterval(check);
            events.forEach((e) => window.removeEventListener(e, bump));
        };
    }, [timeoutMs, navigate]);
}

export default useInactivityLogout;
